/**
 * Tenant-Branding-Daten: Farben, Logo und Favicon.
 * Wird vom ThemeService genutzt, um CSS Custom Properties auf :root zu setzen.
 */
export interface TenantBranding {
  /** Primärfarbe des Mandanten (z. B. "#1B3A5C"). */
  primaryColor: string;
  /** Akzentfarbe des Mandanten (z. B. "#D4A853"). */
  accentColor: string;
  /** Inline-SVG des Mandanten-Logos (weiß – für dunkle Hintergründe). */
  logoSvg: string;
  /** Inline-SVG des Mandanten-Logos (dunkel – für helle Hintergründe). Optional. */
  logoSvgDark?: string;
  /** Anzeigename des Mandanten im App-Header. */
  logoText: string;
  /** Farbe für das Browser-Favicon. */
  faviconColor: string;
  /** Offizielle Webseite des Mandanten (z. B. "www.ligna-systems.com"). Wird im Dokument-Footer verwendet. */
  webseite?: string;
  /** Optionale Tenant-spezifische CSS Custom Properties, die zusätzlich auf :root gesetzt werden. */
  cssVars?: Record<string, string>;
}

/**
 * Schichtzeiten-Konfiguration für einen Schichttyp.
 */
export interface Schichtzeit {
  /** Beginn der Schicht (HH:mm). */
  von: string;
  /** Ende der Schicht (HH:mm). */
  bis: string;
  /** Netto-Arbeitsstunden pro Schicht. */
  stundenProSchicht: number;
}

/**
 * Zonen-Definition (z. B. "Planung & Engineering" oder "Produktion & Logistik").
 */
export interface Zone {
  /** Eindeutige ID der Zone. */
  id: string;
  /** Anzeigename der Zone. */
  name: string;
  /** Typ: "Aufgabe" oder "Zeit". */
  typ: 'Aufgabe' | 'Zeit';
  /** Sortierreihenfolge. */
  reihenfolge: number;
}

/**
 * Mengeneinheit-Definition für einen Hauptbereich (z. B. m³, kg, Stk).
 * Wird für die Mengenplanung im Wizard und in der Gantt-Ansicht verwendet.
 */
export interface MengenEinheit {
  /** Einheit (z. B. "m³", "kg", "Stk"). */
  einheit: string;
  /** Standard-Produktionsrate (z. B. 3.5 für m³/h). */
  rateDefault?: number;
  /** Beschreibung der Rate (z. B. "m³/h Hundegger"). */
  rateBeschreibung?: string;
}

/**
 * Hauptbereich innerhalb einer Zone (z. B. Abbund, Stahl, Montage).
 */
export interface Hauptbereich {
  /** Eindeutige ID des Hauptbereichs. */
  id: string;
  /** Anzeigename. */
  name: string;
  /** Zugehörige Zone-ID. */
  zoneId: string;
  /** Sortierreihenfolge. */
  reihenfolge: number;
  /** Handelt es sich um einen externen Bereich? */
  extern?: boolean;
  /** Ist dieser Bereich der Engpassmaster? */
  istEngpassmaster?: boolean;
  /** Verfügbare Schichtmodelle (z. B. ["Früh","Spät"]). */
  schichtmodelle?: string[];
  /** Nur bei Aufträgen des angegebenen Typs aktiv. */
  nurBeiTyp?: string;
  /** Mengeneinheit für die Produktionsplanung (optional). */
  mengenEinheit?: MengenEinheit;
  /** Bereichstyp: singleton (Statik, Konstruktion), container (Stahl, Abbund), ansicht (AV). */
  typ?: 'singleton' | 'container' | 'ansicht';
}

/**
 * Ressourcen-Definition (Maschine oder Mitarbeitergruppe).
 */
export interface Ressource {
  /** Eindeutige Config-ID der Ressource. */
  id: string;
  /** Anzeigename (z. B. "Hundegger"). */
  name: string;
  /** Typ: "Maschine" oder "Mitarbeitergruppe". */
  typ: 'Maschine' | 'Mitarbeitergruppe';
  /** Optionale Mindestbesetzung je Schicht. */
  besetzung?: Array<{ schicht: string; mindestPersonen: number }>;
}

/**
 * Ressourcen-Referenz innerhalb eines Block-Konfigurations-Schritts.
 */
export interface BlockConfigRessource {
  /** ID der benötigten Ressource (aus Ressource.id). */
  ressourceId: string;
  /** Benötigte Menge dieser Ressource. */
  menge: number;
}

/**
 * Konfiguration eines einzelnen Arbeitsschritts innerhalb einer BlockConfig.
 */
export interface BlockConfigSchritt {
  /** Anzeigename des Schritts (z. B. "Hundegger", "Plasma schneiden"). */
  name: string;
  /** Ist dieser Schritt zwingend erforderlich? */
  pflicht: boolean;
  /** Wird dieser Schritt immer extern ausgeführt? */
  extern?: boolean;
  /** Kann dieser Schritt optional extern vergeben werden? */
  externMoeglich?: boolean;
  /** Standard-Dauer in Arbeitstagen (relevant für externe Schritte). */
  dauerTageDefault?: number;
  /** Ressourcen die dieser Schritt benötigt. */
  ressourcen?: BlockConfigRessource[];
}

/**
 * Block-Konfiguration für einen Block (ehem. Hauptbereich).
 *
 * Definiert welche Arbeitsschritte für einen Block existieren.
 * Bei Blöcken mit mehreren Blocktypen (z. B. Vormontage) kommen
 * die Schritte aus `blockTypSchritte`, andernfalls aus `schritte`.
 */
export interface BlockConfig {
  /** Referenz auf den Block (entspricht Block.id in der Config). */
  blockId: string;
  /** Standard-Schritte für diesen Hauptbereich (wenn kein blockTypSchritte). */
  schritte?: BlockConfigSchritt[];
  /** Schritte je Blocktyp (z. B. { "Einfach": [...], "FWB": [...] }). */
  blockTypSchritte?: Record<string, BlockConfigSchritt[]>;
}

/**
 * Aktivitäts-Definition für die Stundenerfassung (V2).
 */
export interface Aktivitaet {
  /** Eindeutige ID. */
  id: string;
  /** Anzeigename. */
  name: string;
  /** Muss eine Aktivität immer einem Block zugeordnet sein? */
  erfordertBlock: boolean;
}

/**
 * Einzelner Schritt einer Vorbereitungsaufgabe.
 */
export interface VorbereitungsaufgabeSchritt {
  /** Anzeigename des Schritts. */
  name: string;
  /** Ist dieser Schritt zwingend erforderlich? */
  pflicht: boolean;
  /** Welche Rolle wird bei Abschluss benachrichtigt? (Optional) */
  benachrichtigtRolle?: string;
}

/**
 * Vorbereitungsaufgabe fuer automatisch generierte VorlaufAufgaben.
 */
export interface Vorbereitungsaufgabe {
  /** Eindeutige ID der Aufgabe. */
  id: string;
  /** Anzeigename. */
  name: string;
  /** Beschreibung der Aufgabe. */
  beschreibung?: string;
  /** Typ: Arbeitsschritt oder Beschaffung. */
  typ: 'Arbeitsschritt' | 'Beschaffung';
  /** Ist die Aufgabe Pflicht? */
  pflicht: boolean;
  /** Wiederholung: proArbeitsblock oder global. */
  wiederholung?: 'proArbeitsblock' | 'global';
  /** Arbeitstage vor PA-Start: wann Vorlauf beginnen soll. */
  vorlaufStartTage: number;
  /** Arbeitstage vor PA-Start: wann Vorlauf fertig sein muss. */
  vorlaufFertigTage: number;
  /** Schwelle in AT fuer Gelb-Warnung. */
  konfliktSchwelleGelb: number;
  /** Schwelle in AT fuer Rot-Warnung. */
  konfliktSchwelleRot: number;
  /** Nur fuer bestimmten Block-Config generieren (Hauptbereich-ID). */
  nurFuerBlockConfigId?: string;
  /** Ist ein Lieferant erforderlich? */
  lieferantRequired?: boolean;
  /** Standard-Lieferant Config-ID. */
  standardLieferantConfigId?: string | null;
  /** Schritte der Vorbereitungsaufgabe. */
  schritte: VorbereitungsaufgabeSchritt[];
}

/**
 * Block-Aktivierungsregel: automatische Aktivierung eines Blocks
 * wenn ein anderer Block einen bestimmten Zustand erreicht.
 */
export interface BlockAktivierungsRegel {
  /** Trigger: welcher Block loest die Aktivierung aus. */
  trigger: { blockId: string; arbeitsblockTyp: string | null };
  /** Ziel: welcher Block wird aktiviert. */
  aktiviert: { blockId: string; arbeitsblockTyp: string | null };
  /** Beschreibung der Regel. */
  beschreibung?: string;
}

/**
 * Eskalationsstufe fuer ueberfaellige VorlaufAufgaben.
 */
export interface EskalationStufe {
  /** Ab wie vielen Tagen Ueberfaelligkeit greift diese Stufe. */
  tageUeberfaellig: number;
  /** Empfaenger der Eskalation. */
  empfaenger: Array<{ typ: string; wert?: string }>;
  /** Kommunikationskanal. */
  kanal: string[];
  /** Erstellt einen Konflikt-Eintrag? */
  erstelltKonflikt?: boolean;
}

/**
 * Eskalations-Konfiguration.
 */
export interface EskalationConfig {
  /** Cron-Ausdruck fuer den Eskalations-Check. */
  cron: string;
  /** Eskalationsstufen. */
  stufen: EskalationStufe[];
  /** Zusaetzliche Task-Eskalation. */
  taskEskalation?: {
    tageUeberfaellig: number;
    nurPrioritaet?: string;
    empfaenger: Array<{ typ: string; wert?: string }>;
  };
}

/**
 * Vollständige Mandanten-Konfiguration.
 * Wird einmalig beim App-Start geladen und im TenantConfigService bereitgestellt.
 */
export interface TenantConfig {
  /** Eindeutige ID des Mandanten. */
  tenantId: string;
  /** Anzeigename des Mandanten. */
  name: string;
  /** ISO-Ländercode (z. B. "BE"). */
  land: string;
  /** Anzahl Arbeitstage pro Woche. */
  arbeitstageProWoche: number;
  /** Regionale Feiertagsregel (z. B. "BE"). */
  feiertagsregion: string;
  /** Branding-Daten: Farben, Logo. */
  branding: TenantBranding;
  /** Schichtzeiten-Definitionen je Schichtname. */
  schichtzeiten: Record<string, Schichtzeit>;
  /** Zonen-Definitionen. */
  zonen: Zone[];
  /** Hauptbereich-Definitionen. */
  hauptbereiche: Hauptbereich[];
  /** Ressourcen-Definitionen. */
  ressourcen: Ressource[];
  /** Verfügbare Auftragstypen (z. B. ["Bausatz","MitMontage"]). */
  auftragsTypen: string[];
  /** Aktivitäts-Definitionen für die Stundenerfassung. */
  aktivitaeten: Aktivitaet[];
  /** Block-Konfigurationen je Hauptbereich (Arbeitsschritte pro Bereich). */
  blockConfigs?: BlockConfig[];

  /**
   * Gibt an woher Projekte kommen.
   * - `"intern"` (Standard): Projekte werden direkt im System gepflegt (voller CRUD).
   * - `"extern"`: Projekte kommen aus einer externen Datenbank und werden
   *   nur synchronisiert – kein Anlegen/Bearbeiten/Löschen über die UI.
   */
  projektQuelle?: 'intern' | 'extern';

  /**
   * WhatsApp-Modus des Tenants.
   * - `"zentral"` (Standard): Eine Nummer fuer den gesamten Tenant. KI erkennt Projekt aus Nachricht-Kontext.
   * - `"proProjekt"`: Jedes Projekt bekommt eine eigene Twilio-Nummer.
   * Nur vom SystemAdmin aenderbar.
   */
  whatsappModus?: 'zentral' | 'proProjekt';

  /** Tenant-Modus: bestimmt welche Features/Felder sichtbar sind. */
  modus?: 'Baustelle' | 'Betrieb' | 'Werkstatt';

  /**
   * Ob die Projektnummer automatisch vergeben wird (Format: JJ-NNNN, z.B. "26-0001").
   * Default: true (automatisch). Bei false muss der User die Nummer manuell eingeben.
   */
  projektnummerAutomatisch?: boolean;

  /**
   * Ob Felix (KI-Assistent) Projekte anlegen darf (via Chat oder WhatsApp).
   * Default: true.
   */
  felixProjekteAnlegen?: boolean;

  /** Gewerk/Branche des Tenants (z.B. 'Dachdecker', 'SHK'). Bestimmt Felix Branchen-Skills. */
  gewerk?: string;

  /** Firmenspezifisches Wissen fuer Felix (max. 2000 Zeichen). Wird bei jeder Antwort beruecksichtigt. */
  felixFachwissen?: string;

  /** Firmenadresse (fuer PDF-Briefkopf). */
  adresse?: string;
  /** Telefonnummer der Firma (fuer PDF-Briefkopf). */
  telefon?: string;
  /** E-Mail-Adresse der Firma (fuer PDF-Briefkopf). */
  email?: string;

  /** Feature-Toggles: welche Funktionen sind fuer diesen Tenant aktiviert. */
  features?: Record<string, boolean>;

  /** Vorbereitungsaufgaben fuer automatisch generierte VorlaufAufgaben. */
  vorbereitungsaufgaben?: Vorbereitungsaufgabe[];

  /** Block-Aktivierungsregeln. */
  blockAktivierungsRegeln?: BlockAktivierungsRegel[];

  /** Eskalations-Konfiguration fuer ueberfaellige Aufgaben. */
  eskalation?: EskalationConfig;
}

/**
 * Standard-Branding-Konfiguration als Fallback,
 * wenn die API nicht erreichbar ist.
 */
export const DEFAULT_TENANT_BRANDING: TenantBranding = {
  primaryColor: '#e8741e',
  accentColor: '#ffffff',
  logoSvg: '',
  logoText: 'BauAgent.ai',
  faviconColor: '#e8741e'
};

/**
 * Minimale Fallback-Konfiguration für den Offline-Betrieb.
 */
export const DEFAULT_TENANT_CONFIG: TenantConfig = {
  tenantId: 'default',
  name: 'Bauagent.ai',
  land: 'DE',
  arbeitstageProWoche: 5,
  feiertagsregion: 'DE',
  branding: DEFAULT_TENANT_BRANDING,
  schichtzeiten: {
    Tagesschicht: { von: '07:00', bis: '17:00', stundenProSchicht: 9 }
  },
  zonen: [],
  hauptbereiche: [],
  ressourcen: [],
  auftragsTypen: ['Standard'],
  aktivitaeten: []
};
